import { useUpload } from "@/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useTranslation } from "@/components/i18n/Translator";
import { useLayoutMaxWidth } from "@/hooks/useLayoutMaxWidth";
import { useAuth } from "@/client/api";
import { useConfig } from "@/client/useConfig";
import { threadHistoryState } from "@/client/state";
import { useChatData } from "@/client/useChatData";
import { useChatInteract } from "@/client/useChatInteractV2";
import { useChatMessages } from "@/client/useChatMessages";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { loadingState } from "@/client/state";

import { Alert, Box, Stack } from "@mui/material";

import { ErrorBoundary } from "@/components/atoms/ErrorBoundary";
import { Translator } from "@/components/i18n";
import { TaskList } from "@/components/molecules/tasklist/TaskList";

import { IAttachment, attachmentsState } from "@/state/chat";

import ScrollContainer from "@/components/molecules/messages/ScrollContainer";
import Messages from "./Messages";
import DropScreen from "./dropScreen";
import InputBox from "./inputBox";
// import WelcomeScreen from "./welcomeScreen";
import WelcomeMessage from "./welcomeScreen/WelcomeMessage";
// import WaterMark from "./inputBox/waterMark";
import { settingsState } from "@/state/settings";

interface Props {
  companyId?: string | number | undefined;
}

const Chat = ({ companyId }: Props) => {
  const { user } = useAuth();
  const userToken = useAuth();
  const { config } = useConfig();
  const setAttachments = useSetRecoilState(attachmentsState);
  const setThreads = useSetRecoilState(threadHistoryState);
  const setLoading = useSetRecoilState(loadingState);
  // const isMobile = useMediaQuery("(max-width:66rem)");

  const [autoScroll, setAutoScroll] = useState(true);
  const { error, disabled } = useChatData();
  const { uploadFile, sendFileUploadSuccessMessage } = useChatInteract();
  const uploadFileRef = useRef(uploadFile);
  const navigate = useNavigate();

  const [_, setSettings] = useRecoilState(settingsState);
  const enableHistory = !!userToken.accessToken && !!config?.dataPersistence;

  const setChatHistoryOpen = (open: boolean) =>
    setSettings((prev) => ({
      ...prev,
      isChatHistoryOpen: open,
    }));

  const setSidebarOpen = (open: boolean) =>
    setSettings((prev) => ({
      ...prev,
      isSideBarOpen: open,
    }));

  useEffect(() => {
    setChatHistoryOpen(enableHistory);

    return () => {
      // This runs when Chat component is unmounted
      setSidebarOpen(false);
      setChatHistoryOpen(false);
      setSettings((prev) => {
        if (!prev.isTextEditorOpen) {
          return { ...prev, isChatHistoryOpen: true };
        }
        return { ...prev, isChatHistoryOpen: false };
      });
    };
  }, []);

  const fileSpec = useMemo(
    () => ({
      // max_size_mb:
      //   config?.features?.spontaneous_file_upload?.max_size_mb || 500,
      max_files: config?.features?.spontaneous_file_upload?.max_files || 20,
      accept: config?.features?.spontaneous_file_upload?.accept || [],
    }),
    [config]
  );

  const { t } = useTranslation();
  const layoutMaxWidth = useLayoutMaxWidth();

  useEffect(() => {
    uploadFileRef.current = uploadFile;
  }, [uploadFile]);

  const onFileUpload = useCallback(
    (payloads: File[]) => {
      const attachements: IAttachment[] = payloads.map((file) => {
        setLoading(true);
        const id = uuidv4();

        const { xhr, promise } = uploadFileRef.current(file, (progress) => {
          setAttachments((prev) =>
            prev.map((attachment) => {
              if (attachment.id === id) {
                return {
                  ...attachment,
                  uploadProgress: progress,
                };
              }
              return attachment;
            })
          );
        });

        promise
          .then((res) => {
            sendFileUploadSuccessMessage(file.name);
            setAttachments((prev) =>
              prev.map((attachment) => {
                if (attachment.id === id) {
                  return {
                    ...attachment,
                    // Update with the server ID
                    serverId: res.data,
                    uploaded: true,
                    uploadProgress: 100,
                    cancel: undefined,
                  };
                }
                return attachment;
              })
            );
          })
          .catch((error) => {
            toast.error(
              `${t("components.organisms.chat.index.failedToUpload")} ${
                file.name
              }: ${error.message}`
            );
            setAttachments((prev) =>
              prev.filter((attachment) => attachment.id !== id)
            );
          })
          .finally(() => {
            setLoading(false);
          });

        return {
          id,
          type: file.type,
          name: file.name,
          size: file.size,
          uploadProgress: 0,
          cancel: () => {
            toast.info(
              `${t("components.organisms.chat.index.cancelledUploadOf")} ${
                file.name
              }`
            );
            xhr.abort();
            setAttachments((prev) =>
              prev.filter((attachment) => attachment.id !== id)
            );
          },
          remove: () => {
            setAttachments((prev) =>
              prev.filter((attachment) => attachment.id !== id)
            );
          },
        };
      });
      setAttachments((prev) => prev.concat(attachements));
    },
    [uploadFile]
  );

  const onFileUploadError = useCallback(
    (error: string) => toast.error(error),
    [toast]
  );

  const upload = useUpload({
    spec: fileSpec,
    onResolved: onFileUpload,
    onError: onFileUploadError,
    options: { noClick: true },
  });

  const { threadId, messages } = useChatMessages();
  const configLoaded = !!config;

  useEffect(() => {
    const currentPage = new URL(window.location.href);

    if (
      user &&
      config?.dataPersistence &&
      threadId &&
      currentPage.pathname === "/"
    ) {
      navigate(`/thread/${threadId}`);
    } else {
      setThreads((prev) => ({
        ...prev,
        currentThreadId: threadId,
      }));
    }
  }, []);

  const enableMultiModalUpload =
    !disabled && config?.features?.spontaneous_file_upload?.enabled;

  return (
    <Box
      {...(enableMultiModalUpload
        ? upload?.getRootProps({ className: "dropzone" })
        : {})}
      // Disable the onFocus and onBlur events in react-dropzone to avoid interfering with child trigger events
      onBlur={undefined}
      onFocus={undefined}
      display="flex"
      width="100%"
      flexGrow={1}
      position="relative"
      sx={{
        alignItems:
          configLoaded && !threadId && (!messages || messages.length === 0)
            ? "center"
            : undefined,
      }}
    >
      {upload ? (
        <>
          <input id="#upload-drop-input" {...upload.getInputProps()} />
          {upload?.isDragActive ? <DropScreen /> : null}
        </>
      ) : null}
      <Stack width="100%" height="100%">
        {error ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: layoutMaxWidth,
              mx: "auto",
              my: 2,
            }}
          >
            <Alert sx={{ mx: 2 }} id="session-error" severity="error">
              <Translator path="components.organisms.chat.index.couldNotReachServer" />
            </Alert>
          </Box>
        ) : null}
        <TaskList isMobile={true} />

        {/* {configLoaded && !threadId && (!messages || messages.length === 0) && ( */}

        {/* )} */}

        <ErrorBoundary>
          <ScrollContainer
            autoScroll={autoScroll}
            setAutoScroll={setAutoScroll}
          >
            <Stack
              direction="column"
              spacing={2}
              sx={{
                width: "100%",
                maxWidth: layoutMaxWidth,
                mx: "auto",
                flexGrow: 1,
                minHeight: "100%", // Ensure it fills the container
              }}
            >
              {/* WelcomeMessage at top */}
              <WelcomeMessage />

              {/* Spacer to push messages to the bottom if any */}
              {messages && messages.length > 0 && <Box flexGrow={1} />}

              {/* Messages rendered from bottom upward */}
              <Messages />
            </Stack>
          </ScrollContainer>

          <InputBox
            fileSpec={fileSpec}
            onFileUpload={onFileUpload}
            onFileUploadError={onFileUploadError}
            autoScroll={autoScroll}
            setAutoScroll={setAutoScroll}
            page="session"
            companyId={companyId}
          />
          {/* <WaterMark /> */}
        </ErrorBoundary>
      </Stack>
    </Box>
  );
};

export default Chat;
